import React, { useEffect, useState, useContext, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
      CCard,
      CCardBody,
      CNav,
      CNavItem,
      CNavLink,
      CTabContent,
      CTabPane,
      CBadge,
      CButton,
      CSpinner,
      CModal,
      CModalHeader,
      CModalBody,
      CModalFooter,
      CCollapse
} from '@coreui/react';
import Logger from '../../../utils/Logger';
import { ActiveLinkContext } from '../../../components/ActiveLinkContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getDatabase, ref, get, query, orderByChild, equalTo, set, update } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { AuthUserContext } from '../../session';
import config from '../../../utils/EnvironmentSwitcher';
import { fetchWithFabricRetry } from '../../../utils/fabricReEnroll';
import { useCart } from '../../../context/CartContext';
import { getFunctions, httpsCallable } from 'firebase/functions';

const PaymentStatusModal = ({ show, onHide, paymentDetails }) => {
      const getStatusBadgeColor = (status) => {
            switch (status?.toLowerCase()) {
                  case 'complete':
                  case 'paid':
                        return 'success';
                  case 'pending':
                        return 'warning';
                  case 'failed':
                        return 'danger';
                  default:
                        return 'secondary';
            }
      };

      return (
            <CModal
                  visible={show}
                  onClose={onHide}
                  alignment="center"
            >
                  <CModalHeader closeButton>
                        <h5>Payment Status</h5>
                  </CModalHeader>
                  <CModalBody>
                        {paymentDetails ? (
                              <div className="payment-details">
                                    <div className="mb-3 text-center">
                                          <i className={`fas fa-${paymentDetails.status === 'complete' ? 'check-circle text-success' : 'info-circle text-warning'} fa-3x`}></i>
                                    </div>

                                    <div className="mb-3">
                                          <strong>Status:</strong>{' '}
                                          <CBadge color={getStatusBadgeColor(paymentDetails.status)}>
                                                {paymentDetails.status}
                                          </CBadge>
                                    </div>

                                    <div className="mb-3">
                                          <strong>Payment Status:</strong>{' '}
                                          <CBadge color={paymentDetails.payment_status === 'paid' ? 'success' : 'warning'}>
                                                {paymentDetails.payment_status}
                                          </CBadge>
                                    </div>

                                    {paymentDetails.customer_email && (
                                          <div className="mb-3">
                                                <strong>Email:</strong>{' '}
                                                <span className="mb-2">
                                                      {paymentDetails.customer_email}
                                                </span>
                                          </div>
                                    )}

                                    {paymentDetails.message && (
                                          <div className="alert alert-info">
                                                {paymentDetails.message}
                                          </div>
                                    )}
                              </div>
                        ) : (
                              <div className="text-center mb-2">
                                    No payment details available
                              </div>
                        )}
                  </CModalBody>
                  <CModalFooter>
                        <CButton
                              color="primary"
                              onClick={onHide}
                        >
                              Return to Marketplace
                        </CButton>
                  </CModalFooter>
            </CModal>
      );
};

const OrderDetails = ({ order, onReturnRequest }) => {
      return (
            <div className="mt-3 border-top pt-3">
                  {order.items.map((item, index) => (
                        <div key={index} className="d-flex mb-2">
                              <img
                                    src={`https://ipfs.mieros.cloud/ipfs/${item.metadata?.image}`}
                                    alt={item.title}
                                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                    className="me-2"
                              />
                              <div>
                                    <div>{item.title}</div>
                                    <div className="mb-2 small">${item.price}</div>
                              </div>
                        </div>
                  ))}

                  {order.status === 'complete' && (
                        <div className="mt-3">
                              <CButton
                                    color="danger"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => onReturnRequest(order.id)}
                              >
                                    Request Return
                              </CButton>
                        </div>
                  )}
            </div>
      );
};

const OrderCard = ({ order }) => {
      const [showDetails, setShowDetails] = useState(false);

      // Check if this is a Mieros-only order
      const isMierosOnly = order.payment_method === 'mieros_tokens' || order.currency === 'mieros';

      // Format currency - convert cents to dollars
      const formatCurrency = (amountInCents) => {
            const amountInDollars = amountInCents / 100;
            return new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
            }).format(amountInDollars);
      };

      // Format date - handle both 'created' (string) and 'timestamp' (number)
      const formatDate = (dateValue) => {
            let date;
            if (typeof dateValue === 'number') {
                  date = new Date(dateValue);
            } else if (typeof dateValue === 'string') {
                  date = new Date(dateValue);
            } else {
                  return 'N/A';
            }

            if (isNaN(date.getTime())) {
                  return 'Invalid Date';
            }

            return date.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
            });
      };

      // Get order ID - prefer unique orderNumber, fallback to transactionId/paymentId
      const getOrderId = () => {
            // Use unique orderNumber if available (new orders)
            if (order.orderNumber) {
                  // Display a shorter, more readable version: ORD-{last8chars}
                  const parts = order.orderNumber.split('-');
                  if (parts.length >= 2) {
                        // Show last 8 chars of timestamp + random for readability
                        const timestampPart = parts[1];
                        const randomPart = parts[2] || '';
                        return `${parts[0]}-${timestampPart.slice(-6)}${randomPart.slice(0, 2)}`;
                  }
                  return order.orderNumber;
            }
            // Fallback for older orders without orderNumber
            if (order.paymentId) return order.paymentId.substring(0, 8);
            if (order.transactionId) return order.transactionId.substring(0, 8);
            if (order.id) return order.id.substring(0, 8);
            return 'N/A';
      };

      // Get full order number for display in details
      const getFullOrderNumber = () => {
            if (order.orderNumber) return order.orderNumber;
            if (order.paymentId) return order.paymentId;
            if (order.transactionId) return order.transactionId;
            if (order.id) return order.id;
            return 'N/A';
      };

      // Get order date (created or timestamp)
      const getOrderDate = () => {
            if (order.created) return order.created;
            if (order.timestamp) return order.timestamp;
            return null;
      };

      // Get amount display
      const getAmountDisplay = () => {
            if (isMierosOnly) {
                  let mierosAmount = order.mieros_amount || 0;

                  // Fallback: If amount is 0, try to calculate from line_items
                  // This helps fix existing orders that were saved with 0
                  if (mierosAmount === 0 && order.line_items && order.line_items.length > 0) {
                        // Try to get mierosPrice from line_items (stored when order was created)
                        const calculatedAmount = order.line_items.reduce((sum, item) => {
                              // Check mierosPrice field first, then metadata, then default to 0
                              const itemMierosPrice = Number(item.mierosPrice) ||
                                    Number(item.product_metadata?.mierosPrice) || 0;
                              return sum + (itemMierosPrice * (item.quantity || 1));
                        }, 0);

                        if (calculatedAmount > 0) {
                              mierosAmount = calculatedAmount;
                              Logger.debug('Calculated MiEROS amount from line_items:', {
                                    orderId: order.id,
                                    calculatedAmount,
                                    originalAmount: order.mieros_amount,
                                    lineItems: order.line_items.map(item => ({
                                          name: item.product_name,
                                          mierosPrice: item.mierosPrice || item.product_metadata?.mierosPrice
                                    }))
                              });
                        }
                  }

                  return mierosAmount > 0 ? `${mierosAmount} MiEROS` : 'Free';
            } else {
                  // For USD orders, amount might be in cents or dollars
                  const amount = order.amount || order.amount_total || 0;
                  // If amount is less than 100, assume it's already in dollars, otherwise assume cents
                  return amount < 100 ? formatCurrency(amount * 100) : formatCurrency(amount);
            }
      };

      // Get status badge color
      const getStatusBadgeColor = () => {
            const status = order.status?.toLowerCase();
            if (status === 'complete' || status === 'paid') return 'success';
            if (status === 'pending') return 'warning';
            if (status === 'failed') return 'danger';
            return 'secondary';
      };

      return (
            <CCard className="mb-3">
                  <CCardBody>
                        <div className="d-flex justify-content-between align-items-center">
                              <div>
                                    <h6 className="mb-1">
                                          Order #{getOrderId()}
                                          {isMierosOnly && (
                                                <CBadge color="info" className="ms-2">MiEROS</CBadge>
                                          )}
                                    </h6>
                                    <div className="mb-2 small">
                                          {getOrderDate() ? formatDate(getOrderDate()) : 'N/A'}
                                    </div>
                              </div>
                              <div className="text-end">
                                    <CBadge color={getStatusBadgeColor()}>
                                          {order.status?.toUpperCase() || 'UNKNOWN'}
                                    </CBadge>
                                    <div className="mt-2 h5">
                                          {getAmountDisplay()}
                                    </div>
                              </div>
                        </div>

                        <CCollapse visible={showDetails}>
                              <div className="mt-3 border-top pt-3">
                                    <div className="row">
                                          <div className="col-md-6">
                                                {order.orderNumber && (
                                                      <div className="mb-2">
                                                            <strong>Order Number:</strong>{' '}
                                                            <span className="mb-2 font-monospace">{order.orderNumber}</span>
                                                      </div>
                                                )}
                                                {order.paymentId && (
                                                      <div className="mb-2">
                                                            <strong>Payment ID:</strong>{' '}
                                                            <span className="mb-2">{order.paymentId}</span>
                                                      </div>
                                                )}
                                                {order.transactionId && (
                                                      <div className="mb-2">
                                                            <strong>Transaction ID:</strong>{' '}
                                                            <span className="mb-2">{order.transactionId}</span>
                                                      </div>
                                                )}
                                                {order.sessionId && (
                                                      <div className="mb-2">
                                                            <strong>Session ID:</strong>{' '}
                                                            <span className="mb-2">{order.sessionId}</span>
                                                      </div>
                                                )}
                                                {order.payment_method && (
                                                      <div className="mb-2">
                                                            <strong>Payment Method:</strong>{' '}
                                                            <CBadge color="info">{order.payment_method}</CBadge>
                                                      </div>
                                                )}
                                                {order.mode && (
                                                      <div className="mb-2">
                                                            <strong>Mode:</strong>{' '}
                                                            <CBadge color="info">{order.mode}</CBadge>
                                                      </div>
                                                )}
                                                {isMierosOnly && (
                                                      <div className="mb-2">
                                                            <strong>MiEROS Amount:</strong>{' '}
                                                            <span className="mb-2">
                                                                  {(() => {
                                                                        let amount = order.mieros_amount || 0;
                                                                        // Fallback calculation from line_items if amount is 0
                                                                        if (amount === 0 && order.line_items && order.line_items.length > 0) {
                                                                              amount = order.line_items.reduce((sum, item) => {
                                                                                    return sum + (Number(item.mierosPrice) || Number(item.product_metadata?.mierosPrice) || 0) * (item.quantity || 1);
                                                                              }, 0);
                                                                        }
                                                                        return amount > 0 ? `${amount} tokens` : '0 tokens (Free)';
                                                                  })()}
                                                            </span>
                                                      </div>
                                                )}
                                                {(order.sellerId || order.recipientId) && (
                                                      <div className="mb-2">
                                                            <strong>Seller / Recipient:</strong>{' '}
                                                            <span className="mb-2 text-truncate d-inline-block" style={{ maxWidth: '200px' }} title={order.sellerId || order.recipientId}>
                                                                  {order.sellerId || order.recipientId}
                                                            </span>
                                                      </div>
                                                )}
                                          </div>
                                          <div className="col-md-6">
                                                {order.email && (
                                                      <div className="mb-2">
                                                            <strong>Email:</strong>{' '}
                                                            <span className="mb-2">{order.email}</span>
                                                      </div>
                                                )}
                                                {order.verified !== undefined && (
                                                      <div className="mb-2">
                                                            <strong>Verification:</strong>{' '}
                                                            <CBadge color={order.verified ? 'success' : 'warning'}>
                                                                  {order.verified ? 'Verified' : 'Pending'}
                                                            </CBadge>
                                                      </div>
                                                )}
                                                {getOrderDate() && (
                                                      <div className="mb-2">
                                                            <strong>Created:</strong>{' '}
                                                            <span className="mb-2">
                                                                  {formatDate(getOrderDate())}
                                                            </span>
                                                      </div>
                                                )}
                                                {order.payment_status && (
                                                      <div className="mb-2">
                                                            <strong>Payment Status:</strong>{' '}
                                                            <CBadge color={order.payment_status === 'paid' ? 'success' : 'warning'}>
                                                                  {order.payment_status}
                                                            </CBadge>
                                                      </div>
                                                )}
                                                {order.line_items && order.line_items.length > 0 && (
                                                      <div className="mb-2">
                                                            <strong>Items:</strong>{' '}
                                                            <span className="mb-2">{order.line_items.length} item(s)</span>
                                                      </div>
                                                )}
                                          </div>
                                    </div>
                                    {order.line_items && order.line_items.length > 0 && (
                                          <div className="mt-3 border-top pt-3">
                                                <strong>Items:</strong>
                                                {order.line_items.map((item, index) => (
                                                      <div key={index} className="d-flex align-items-center mt-2">
                                                            {item.product_metadata?.nftId && (
                                                                  <div className="me-3">
                                                                        <small className="mb-2">
                                                                              NFT ID: {item.product_metadata.nftId.substring(0, 8)}...
                                                                        </small>
                                                                  </div>
                                                            )}
                                                            <div>
                                                                  <div>{item.product_name || 'Unnamed Item'}</div>
                                                                  {item.product_description && (
                                                                        <small className="mb-2">{item.product_description}</small>
                                                                  )}
                                                            </div>
                                                      </div>
                                                ))}
                                          </div>
                                    )}
                              </div>
                        </CCollapse>

                        <div className="text-center mt-3">
                              <CButton
                                    color="link"
                                    onClick={() => setShowDetails(!showDetails)}
                              >
                                    {showDetails ? (
                                          <>
                                                <i className="fas fa-chevron-up me-1"></i>
                                                Show Less
                                          </>
                                    ) : (
                                          <>
                                                <i className="fas fa-chevron-down me-1"></i>
                                                Show Details
                                          </>
                                    )}
                              </CButton>
                        </div>
                  </CCardBody>
            </CCard>
      );
};

const useOrders = () => {
      const [orders, setOrders] = useState([]);
      const [loading, setLoading] = useState(true);
      const { authUser } = useContext(AuthUserContext);
      const fetchOrders = useCallback(async () => {
            try {
                  // Add debug logging
                  Logger.debug('fetchOrders auth state:', {
                        hasAuth: !!authUser,
                        email: authUser?.email
                  });

                  if (!authUser) {
                        setLoading(false);
                        setOrders([]);
                        return;
                  }

                  const db = getDatabase();
                  const purchaseHistoryRef = ref(db, 'purchase_history');
                  const userOrdersQuery = query(
                        purchaseHistoryRef,
                        orderByChild('email'),
                        equalTo(authUser.email)
                  );

                  const ordersSnapshot = await get(userOrdersQuery);

                  const ordersData = [];

                  // Get orders from Firebase
                  if (ordersSnapshot.exists()) {
                        ordersSnapshot.forEach((childSnapshot) => {
                              ordersData.push({
                                    id: childSnapshot.key,
                                    ...childSnapshot.val(),
                                    source: 'firebase'
                              });
                        });
                  }

                  // Also fetch orders indexed by buyerId (more reliable than email alone)
                  const buyerUid = authUser?.uid || authUser?.authUser?.uid;
                  if (buyerUid) {
                        const buyerOrdersQuery = query(
                              purchaseHistoryRef,
                              orderByChild('buyerId'),
                              equalTo(buyerUid)
                        );
                        const buyerSnapshot = await get(buyerOrdersQuery);
                        if (buyerSnapshot.exists()) {
                              buyerSnapshot.forEach((childSnapshot) => {
                                    ordersData.push({
                                          id: childSnapshot.key,
                                          ...childSnapshot.val(),
                                          source: 'firebase-buyerId'
                                    });
                              });
                        }
                  }

                  // Merge blockchain transaction history for this user (MiEROS token transfers / purchases)
                  try {
                        const auth = getAuth();
                        const currentUser = auth.currentUser;
                        const userId = currentUser?.uid || authUser?.uid || authUser?.authUser?.uid;
                        if (userId && currentUser) {
                              const idToken = await currentUser.getIdToken();
                              const txRes = await fetch(`${config.apiUrl}/api/transactions/${userId}`, {
                                    headers: {
                                          Authorization: `Bearer ${idToken}`,
                                          Accept: 'application/json',
                                    },
                              });
                              if (txRes.ok) {
                                    const txData = await txRes.json();
                                    const txArray = Array.isArray(txData)
                                          ? txData
                                          : (txData?.transactions || txData?.data || []);
                                    const existingTxIds = new Set(
                                          ordersData.map((order) => order.transactionId || order.paymentId).filter(Boolean)
                                    );
                                    txArray.forEach((tx) => {
                                          const txId = tx.id || tx.transactionId || tx.txId || tx.txID;
                                          if (!txId || existingTxIds.has(txId)) return;
                                          existingTxIds.add(txId);
                                          ordersData.push({
                                                id: `chain_${txId}`,
                                                transactionId: txId,
                                                status: tx.status || 'complete',
                                                payment_status: 'paid',
                                                payment_method: 'mieros_tokens',
                                                mieros_amount: tx.amount ?? tx.mierosAmount ?? tx.value ?? 0,
                                                timestamp: tx.timestamp || tx.createdAt || Date.now(),
                                                source: 'blockchain',
                                                line_items: [],
                                          });
                                    });
                              }
                        }
                  } catch (chainErr) {
                        Logger.warn('Could not load blockchain transactions for orders:', chainErr);
                  }

                  // Also check localStorage for backup orders (Mieros-only purchases that failed to save to Firebase)
                  try {
                        const localPurchases = JSON.parse(localStorage.getItem('local_purchases') || '[]');
                        const userLocalOrders = localPurchases.filter(
                              purchase => purchase.email === authUser.email && purchase.savedLocally
                        );

                        // Add local orders that aren't already in Firebase (by transactionId)
                        const firebaseTransactionIds = new Set(
                              ordersData.map(order => order.transactionId || order.paymentId).filter(Boolean)
                        );

                        userLocalOrders.forEach(localOrder => {
                              const transactionId = localOrder.transactionId || localOrder.paymentId;
                              if (transactionId && !firebaseTransactionIds.has(transactionId)) {
                                    ordersData.push({
                                          ...localOrder,
                                          id: `local_${transactionId}`,
                                          source: 'localStorage'
                                    });
                                    Logger.info('Added local backup order to orders list:', localOrder);
                              }
                        });
                  } catch (localError) {
                        Logger.warn('Error reading localStorage orders:', localError);
                  }

                  // Deduplicate orders by orderNumber (primary) or transactionId/paymentId (fallback)
                  // Each order should have a unique orderNumber, but we also check transactionId for older orders
                  const seenOrderNumbers = new Set();
                  const seenTransactionIds = new Set();
                  const deduplicatedOrders = [];

                  ordersData.forEach(order => {
                        // Primary: Use orderNumber if available (new orders)
                        if (order.orderNumber) {
                              if (!seenOrderNumbers.has(order.orderNumber)) {
                                    seenOrderNumbers.add(order.orderNumber);
                                    deduplicatedOrders.push(order);
                              } else {
                                    Logger.debug('Skipping duplicate order (same orderNumber):', {
                                          orderNumber: order.orderNumber,
                                          orderId: order.id
                                    });
                              }
                        } else {
                              // Fallback: Use transactionId or paymentId for older orders without orderNumber
                              const transactionId = order.transactionId || order.paymentId;
                              if (transactionId) {
                                    if (!seenTransactionIds.has(transactionId)) {
                                          seenTransactionIds.add(transactionId);
                                          deduplicatedOrders.push(order);
                                    } else {
                                          Logger.debug('Skipping duplicate order (same transactionId):', {
                                                transactionId,
                                                orderId: order.id
                                          });
                                    }
                              } else {
                                    // Last resort: Use Firebase key
                                    const orderKey = `key_${order.id}`;
                                    if (!seenTransactionIds.has(orderKey)) {
                                          seenTransactionIds.add(orderKey);
                                          deduplicatedOrders.push(order);
                                    }
                              }
                        }
                  });

                  // Sort by timestamp (newest first)
                  deduplicatedOrders.sort((a, b) => {
                        const timeA = a.timestamp || (a.created ? new Date(a.created).getTime() : 0);
                        const timeB = b.timestamp || (b.created ? new Date(b.created).getTime() : 0);
                        return timeB - timeA;
                  });

                  if (ordersData.length !== deduplicatedOrders.length) {
                        Logger.info(`Deduplicated orders: ${ordersData.length} -> ${deduplicatedOrders.length}`);
                  }

                  setOrders(deduplicatedOrders);

                  if (ordersData.length === 0) {
                        Logger.info('No orders found for user');
                  } else {
                        Logger.info(`Loaded ${ordersData.length} orders for user`);
                  }
            } catch (error) {
                  Logger.error('Error fetching orders:', error);
                  console.error('Detailed error:', error);
                  setOrders([]);
            } finally {
                  setLoading(false);
            }
      }, [authUser]);

      // Only fetch when we have auth
      useEffect(() => {
            if (authUser) {
                  fetchOrders();
            }
      }, [fetchOrders, authUser]);

      return { orders, loading, fetchOrders };
};

const OrdersPage = () => {
      const [activeTab, setActiveTab] = useState('all');
      const [showPaymentModal, setShowPaymentModal] = useState(false);
      const [paymentDetails, setPaymentDetails] = useState(null);
      const [hasCheckedSession, setHasCheckedSession] = useState(false);
      const [searchParams] = useSearchParams();
      const { authUser, getValidToken } = useContext(AuthUserContext);
      const navigate = useNavigate();
      const { clearCart } = useCart();

      // Add debug logging for component mount
      useEffect(() => {
            Logger.debug('OrdersPage mounted with auth:', {
                  hasAuth: !!authUser,
                  email: authUser?.email
            });
      }, [authUser]);

      const { orders, loading, fetchOrders } = useOrders({ authUser });

      // Custom styles for the nav items
      const navItemStyle = (isActive) => ({
            backgroundColor: isActive ? '#eef2ff' : 'transparent',
            borderRadius: '8px',
            margin: '0 4px',
            transition: 'all 0.3s ease'
      });

      const navLinkStyle = (isActive) => ({
            color: isActive ? '#4f46e5' : '#6b7280',
            fontWeight: isActive ? '600' : '500',
            padding: '8px 16px',
            cursor: 'pointer'
      });

      const filteredOrders = useMemo(() => {
            return orders.filter(order => {
                  const status = order.status?.toLowerCase();
                  switch (activeTab) {
                        case 'pending':
                              return status === 'pending';
                        case 'completed':
                              // Mieros-only orders have status 'complete', USD orders might have 'paid' or 'complete'
                              return status === 'complete' || status === 'paid';
                        case 'returns':
                              return status === 'return_requested';
                        default:
                              return true;
                  }
            });
      }, [orders, activeTab]);

      // Version marker: if you see "Purchase record not found" thrown at handleNFTTransfer,
      // you are running OLD cached code. This build returns { skipped: true } instead of throwing.
      const HANDLE_NFT_TRANSFER_VERSION = 'no-throw-v1-2025-01-26';
      const handleNFTTransfer = async (sessionId) => {
            console.warn('[OrdersPage.handleNFTTransfer] BUILD:', HANDLE_NFT_TRANSFER_VERSION);
            try {
                  const idToken = getValidToken
                        ? await getValidToken()
                        : (await getAuth().currentUser?.getIdToken?.()) ?? null;
                  if (!idToken) throw new Error('Not authenticated');

                  // Purchase record: read from checkout_sessions only. Try (1) root checkout_sessions/<sessionId>,
                  // then (2) verified_creators/checkout_sessions/<sessionId>. Structure: product_id, metadata.{nftId,listingId,sellerID,price}, price_data, status.
                  console.warn('[handleNFTTransfer] CHECKOUT_SESSIONS_ONLY_V2 — if you see purchase_history in logs, hard refresh (Ctrl+Shift+R)');
                  Logger.info('[handleNFTTransfer] Reading checkout_sessions', { sessionIdStart: sessionId?.slice(0, 24) });

                  let purchaseData = null;
                  const db = getDatabase();
                  const pathsToTry = [
                        `checkout_sessions/${sessionId}`,
                        `verified_creators/checkout_sessions/${sessionId}`
                  ];

                  for (const path of pathsToTry) {
                        if (purchaseData) break;
                        try {
                              Logger.info('[handleNFTTransfer] Trying path', { path });
                              const sessionRef = ref(db, path);
                              const sessionSnap = await get(sessionRef);
                              Logger.info('[handleNFTTransfer] Read result', {
                                    path,
                                    exists: sessionSnap.exists(),
                                    keys: sessionSnap.exists() ? Object.keys(sessionSnap.val() || {}) : []
                              });
                              if (sessionSnap.exists()) {
                                    const raw = sessionSnap.val();
                                    const nftId = raw.product_id || raw.metadata?.nftId;
                                    // listingId should be distinct from nftId; only use it when backend provides it
                                    const listingId = raw.metadata?.listingId || raw.metadata?.listingID || null;
                                    // Stripe checkout_sessions store amount_total in cents; fallback to price_data/metadata.price (dollars) if needed.
                                    const rawAmount = raw.amount_total ?? null;
                                    let priceCents = 0;
                                    if (typeof rawAmount === 'number') {
                                          priceCents = rawAmount;
                                    } else {
                                          const priceStr = raw.price_data ?? raw.metadata?.price ?? '0';
                                          priceCents = Math.round(parseFloat(String(priceStr).replace(/[^0-9.-]/g, '')) * 100) || 0;
                                    }

                                    purchaseData = {
                                          sessionId,
                                          status: raw.status ?? 'complete',
                                          payment_status: raw.payment_status ?? 'paid',
                                          line_items: nftId
                                                ? [{
                                                      price: priceCents,
                                                      quantity: 1,
                                                      product_metadata: {
                                                            nftId,
                                                            listingId,
                                                      },
                                                      nftId,
                                                      listingId,
                                                }]
                                                : [],
                                    };
                                    Logger.info('[handleNFTTransfer] Built purchaseData from checkout_sessions', {
                                          path,
                                          nftId,
                                          listingId,
                                          priceCents,
                                          lineItemsCount: purchaseData.line_items?.length ?? 0
                                    });
                                    break;
                              }
                        } catch (err) {
                              Logger.info('[handleNFTTransfer] Read failed for path', {
                                    path,
                                    error: err?.message || String(err)
                              });
                        }
                  }

                  if (!purchaseData) {
                        Logger.info('[handleNFTTransfer] No purchase record found — returning skipped (no error thrown)', {
                              sessionIdSample: sessionId?.slice(0, 30) + '...',
                              reason: 'Purchase record not found',
                              tip: 'Check Firebase path checkout_sessions/<sessionId> exists and rules allow .read for auth (or customer_email)'
                        });
                        return { skipped: true, reason: 'Purchase record not found' };
                  }

                  if (!purchaseData.line_items || purchaseData.line_items.length === 0) {
                        Logger.warn('Purchase record has no line_items:', purchaseData);
                        return { skipped: true, reason: 'No line items' };
                  }

                  Logger.info('[handleNFTTransfer] Purchase record found, processing line items', {
                        lineItemsCount: purchaseData.line_items.length,
                        sessionIdStart: sessionId?.slice(0, 24)
                  });

                  if ((purchaseData.payment_status === 'paid' || purchaseData.payment_status === true) &&
                        (purchaseData.status === 'paid' || purchaseData.status === 'complete')) {
                        // Process each line item
                        const results = [];
                        const meta = (item) => item?.product_metadata ?? {};
                        for (const item of purchaseData.line_items) {
                              const nftId = meta(item).nftId ?? item.nftId;
                              const listingId = meta(item).listingId ?? item.listingId;
                              const sellerId = meta(item).sellerId ?? item.sellerId;
                              if (!nftId) {
                                    Logger.warn('Skipping line item with no nftId:', item);
                                    continue;
                              }
                              try {
                                    const priceCents = Number(item.price) || 0;
                                    const priceInDollars = priceCents / 100;

                                    const response = await fetchWithFabricRetry(`${config.apiUrl}/nft/transfer`, {
                                          method: 'POST',
                                          headers: {
                                                'Content-Type': 'application/json',
                                                'Authorization': `Bearer ${idToken}`
                                          },
                                          body: JSON.stringify({
                                                nftId,
                                                // listingId is distinct from nftId; only send it when known
                                                listingId,
                                                price: priceInDollars,
                                                buyerId: authUser.uid,
                                                sessionId: sessionId
                                          })
                                    });

                                    if (!response.ok) {
                                          const errorData = await response.json().catch(() => ({}));
                                          throw new Error(errorData.message || 'Failed to transfer NFT');
                                    }

                                    const data = await response.json();
                                    results.push({ success: true, nftId, data });
                                    Logger.info(`NFT transfer successful for ${nftId}:`, data);
                              } catch (error) {
                                    Logger.error(`Error transferring NFT ${nftId}:`, error);
                                    results.push({ success: false, nftId, error: error.message });
                              }
                        }

                        // Handle transfer results
                        const successfulTransfers = results.filter(r => r.success);
                        const failedTransfers = results.filter(r => !r.success);

                        if (successfulTransfers.length > 0) {
                              toast.success(`Successfully transferred ${successfulTransfers.length} NFT${successfulTransfers.length > 1 ? 's' : ''}`);
                        }

                        if (failedTransfers.length > 0) {
                              toast.error(`Failed to transfer ${failedTransfers.length} NFT${failedTransfers.length > 1 ? 's' : ''}. Support will assist you.`);
                              Logger.error('Failed transfers:', failedTransfers);
                        }


                  }


                  Logger.info('NFT transfer successful');
                  return { skipped: false };
            } catch (error) {
                  Logger.error('Error in handleNFTTransfer:', error);
                  throw error;
            }
      };


      // Define verifyPayment function with proper auth check
      const verifyPayment = useCallback(async (sessionId) => {
            Logger.debug('[verifyPayment] Called', { sessionId: sessionId?.slice(0, 28) + '...' });
            try {
                  const idToken = getValidToken
                        ? await getValidToken()
                        : (await getAuth().currentUser?.getIdToken?.()) ?? null;
                  if (!idToken) {
                        toast.error('Not authenticated');
                        return;
                  }
                  const response = await fetch(`${config.paymentUrl}/checkout-session-status`, {
                        method: 'POST',
                        headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${idToken}`
                        },
                        body: JSON.stringify({ sessionId })
                  });

                  const text = await response.text();
                  let data;
                  try {
                        data = text ? JSON.parse(text) : {};
                  } catch {
                        data = {};
                  }

                  if (!response.ok) {
                        const message = data.message || data.error || (response.status === 502 || response.status === 503
                              ? 'Payment service temporarily unavailable. Please try again later.'
                              : `Payment verification failed (${response.status})`);
                        Logger.error('Checkout session status error:', { status: response.status, statusText: response.statusText, body: data || text?.slice(0, 200) });
                        throw new Error(message);
                  }

                  setPaymentDetails(data);
                  setShowPaymentModal(true);
                  Logger.debug('Payment details:', { status: data.status, payment_status: data.payment_status });
                  if (data.status === 'complete' && data.payment_status === 'paid') {
                        clearCart();
                        toast.success('Payment successful!');
                        try {
                              const result = await handleNFTTransfer(sessionId);
                              Logger.info('[verifyPayment] handleNFTTransfer result', {
                                    skipped: result?.skipped,
                                    reason: result?.reason,
                                    hasResult: !!result
                              });
                              if (result?.skipped) {
                                    toast.warning(
                                          'Payment confirmed. Your order is being processed; if NFTs don\'t appear in My Stuff shortly, contact support.'
                                    );
                              } else {
                                    toast.success('NFT transfer successful!');
                              }
                        } catch (transferError) {
                              Logger.error('Error in handleNFTTransfer:', transferError);
                              const msg = transferError?.message ?? '';
                              if (msg.includes('Purchase record not found') || msg.includes('Permission denied')) {
                                    toast.warning(
                                          'Payment confirmed. Your order is being processed; if NFTs don\'t appear in My Stuff shortly, contact support.'
                                    );
                              } else {
                                    toast.error(msg || 'NFT transfer could not be completed. Support will assist you.');
                              }
                        }
                  } else {
                        toast.warning(data.message || 'Payment status pending');
                  }

                  // Refresh orders list
                  fetchOrders();
            } catch (error) {
                  Logger.error('Error verifying payment:', error);
                  const msg = error?.message ?? '';
                  if (msg.includes('Purchase record not found') || msg.includes('Permission denied')) {
                        toast.warning(
                              'Payment confirmed. Your order is being processed; if NFTs don\'t appear in My Stuff shortly, contact support.'
                        );
                  } else {
                        toast.error(msg || 'Failed to verify payment status');
                  }
            }
      }, [getValidToken, fetchOrders, clearCart]);

      const sendPaymentConfirmationEmail = async (paymentDetails) => {
            try {
                  const functions = getFunctions();
                  const sendEmail = httpsCallable(functions, 'sendPaymentConfirmation');

                  await sendEmail({
                        email: paymentDetails.customer_email,
                        paymentId: paymentDetails.transaction_id,
                        amount: paymentDetails.amount,
                        items: paymentDetails.items,
                        status: paymentDetails.status,
                        created: new Date().toISOString()
                  });

                  Logger.info('Payment confirmation email sent successfully');
            } catch (error) {
                  Logger.error('Error sending payment confirmation email:', error);
                  // Don't throw error as this is not critical to the payment flow
            }
      };

      // Separate verification for guest checkout
      const verifyGuestPayment = async (sessionId) => {
            try {
                  const response = await fetch(`${config.paymentUrl}/checkout-session-status`, {
                        method: 'POST',
                        headers: {
                              'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                              sessionId,
                              isGuest: true
                        })
                  });

                  if (!response.ok) {
                        throw new Error('Failed to verify payment');
                  }

                  const data = await response.json();
                  setPaymentDetails({
                        ...data,
                        message: 'Thank you for your purchase! A confirmation email has been sent with your transaction details.'
                  });
                  setShowPaymentModal(true);

                  if (data.status === 'complete' && data.payment_status === 'paid') {
                        clearCart();
                        // Send confirmation email
                        await sendPaymentConfirmationEmail(data);
                        toast.success('Payment successful!');
                        setTimeout(() => {
                              navigate('/marketplace');
                        }, 5000);
                  } else {
                        toast.warning(data.message || 'Payment status pending');
                  }
            } catch (error) {
                  Logger.error('Error verifying guest payment:', error);
                  toast.error('Failed to verify payment status');
            }
      };

      // Check for return URL parameters
      useEffect(() => {
            const sessionId = searchParams.get('session_id');
            if (sessionId && !hasCheckedSession) {
                  Logger.debug('[OrdersPage] session_id in URL, verifying payment', {
                        sessionIdStart: sessionId.slice(0, 24),
                        hasAuthUser: !!authUser,
                        willCall: authUser ? 'verifyPayment' : 'verifyGuestPayment'
                  });
                  if (authUser) {
                        verifyPayment(sessionId);
                  } else {
                        verifyGuestPayment(sessionId);
                  }
                  setHasCheckedSession(true);
            }
      }, [searchParams, verifyPayment, hasCheckedSession, authUser]);

      // If this is a return URL and we're processing payment
      if (searchParams.get('session_id')) {
            return (
                  <div className="p-4">
                        <div className="text-center">
                              <CSpinner color="primary" />
                              <p className="mt-3">Processing your payment...</p>
                        </div>
                        {showPaymentModal && paymentDetails && (
                              <PaymentStatusModal
                                    show={showPaymentModal}
                                    onHide={() => {
                                          setShowPaymentModal(false);
                                          navigate('/marketplace');
                                    }}
                                    paymentDetails={paymentDetails}
                              />
                        )}
                        <ToastContainer />
                  </div>
            );
      }

      // Regular orders page for authenticated users
      if (!authUser) {
            return (
                  <div className="p-4">
                        <div className="text-center">
                              <p>Please log in to view your orders.</p>
                              <button
                                    className="btn btn-primary"
                                    onClick={() => navigate('/login')}
                              >
                                    Log In
                              </button>
                        </div>
                  </div>
            );
      }

      return (
            <div className="p-4">
                  <ToastContainer />
                  <h2 className="mb-4">My Orders</h2>

                  <CNav variant="tabs" className="mb-4">
                        <CNavItem style={navItemStyle(activeTab === 'all')}>
                              <CNavLink
                                    active={activeTab === 'all'}
                                    onClick={() => setActiveTab('all')}
                                    style={navLinkStyle(activeTab === 'all')}
                              >
                                    All Orders
                              </CNavLink>
                        </CNavItem>
                        <CNavItem style={navItemStyle(activeTab === 'pending')}>
                              <CNavLink
                                    active={activeTab === 'pending'}
                                    onClick={() => setActiveTab('pending')}
                                    style={navLinkStyle(activeTab === 'pending')}
                              >
                                    Pending
                              </CNavLink>
                        </CNavItem>
                        <CNavItem style={navItemStyle(activeTab === 'completed')}>
                              <CNavLink
                                    active={activeTab === 'completed'}
                                    onClick={() => setActiveTab('completed')}
                                    style={navLinkStyle(activeTab === 'completed')}
                              >
                                    Completed
                              </CNavLink>
                        </CNavItem>
                        <CNavItem style={navItemStyle(activeTab === 'returns')}>
                              <CNavLink
                                    active={activeTab === 'returns'}
                                    onClick={() => setActiveTab('returns')}
                                    style={navLinkStyle(activeTab === 'returns')}
                              >
                                    Returns
                              </CNavLink>
                        </CNavItem>
                  </CNav>
                  {loading ? (
                        <div className="d-flex justify-content-center">
                              <CSpinner color="primary" />
                        </div>
                  ) : (
                        <div className="orders-list">
                              {filteredOrders.map(order => (
                                    <OrderCard
                                          key={order.id}
                                          order={order}
                                    />
                              ))}
                        </div>
                  )}
            </div>
      );
};

export default OrdersPage; 

